import { useQuery } from '@apollo/client'
import { buttonVariants, cn, Cover, SimpleTooltip, Stack } from 'design-system'
import { gqlDisplayName } from 'shared/lib/user'

import { ClipboardCopyButton } from 'src/components/generics/clipboard-copy/button'
import Avatar from 'src/components/user/v2/avatar'
import { UsernameMenu } from 'src/components/user/v2/username-menu/menu'
import { FETCH_BLOCKED_USER } from 'src/graphql/board/blocked-user'
import useBoardId from 'src/hooks/useBoardId'
import { Link } from 'src/lib/router'
import { networkUrl } from 'src/lib/util/network'

import { BlockedUserCardWrapper } from './blocked-user-card-wrapper'
import { BlockedUserError } from './blocked-user-error'
import { BlockedUserInfoGroup } from './blocked-user-info-group'
import { BlockedUserLoading } from './blocked-user-loading'
import { UserBlockReason } from '~gql-types/graphql'
import { BlockedFromBoardExpandables } from './blocked-from-board-expandables'

// @TODO replace when article will be ready
const ARTICLE_LINK =
  'https://support.yellowdig.co/hc/en-us/articles/34945628704020-Managing-Community-Members'
const COMMUNITY_TITLE = 'Community Title:'
const COMMUNITY_URL = 'Community URL:'
const COMMUNITY_OWNER = 'Community Owner:'

const BlockedMessage = ({ reason }: { reason: UserBlockReason }) => {
  if (reason === UserBlockReason.OptOut) {
    return (
      <p className="text-sm font-light text-slate-700">
        It appears{' '}
        <strong className="font-semibold">
          you&apos;ve opted out of a paid Yellowdig membership
        </strong>
        , and as a result, you no longer have access to this community. If this
        was unintentional,{' '}
        <strong className="font-semibold">
          please reach out to the Community Owner for assistance
        </strong>
        .
      </p>
    )
  }

  return (
    <p className="text-sm font-light text-slate-700">
      You&apos;ve been{' '}
      <strong className="font-semibold">blocked from this community </strong>
      and, as a result, you{' '}
      <strong className="font-semibold">no longer have access to it</strong>. If
      this is a mistake, please{' '}
      <strong className="font-semibold">
        reach out to the Community Owner for assistance
      </strong>
      .
    </p>
  )
}

export const BlockedFromBoard = () => {
  const boardId = useBoardId()
  const { data, error, loading } = useQuery(FETCH_BLOCKED_USER, {
    variables: { boardId }
  })

  if (loading) return <BlockedUserLoading />

  if (error) return <BlockedUserError />

  if (!data) return <BlockedUserError />

  const {
    findBoard: { loggedInBoardUser, network, owner, title }
  } = data

  const ownerName = gqlDisplayName(owner)
  const communityUrl = `${networkUrl(network.name, `/community/${boardId}`)}`
  const textToCopy = `${COMMUNITY_TITLE} ${title}\n${COMMUNITY_URL} ${communityUrl}\n${COMMUNITY_OWNER} ${ownerName}`

  return (
    <Cover fullSize className="px-4">
      <BlockedUserCardWrapper>
        <Stack gap={4}>
          <div className="flex justify-center">
            <Avatar user={loggedInBoardUser} />
          </div>
          <Stack gap={3}>
            <h2 className="text-center text-base">
              Your Access to This Community is Restricted
            </h2>
            <Stack
              gap={4}
              className="relative rounded-lg bg-slate-100 px-4 py-2">
              <SimpleTooltip content="Copy Community Title">
                <ClipboardCopyButton
                  text={textToCopy}
                  label="Copy Community Title"
                  className="absolute right-4 top-4 text-blue-600"
                />
              </SimpleTooltip>
              <BlockedUserInfoGroup title={COMMUNITY_TITLE}>
                <p className="text-sm font-medium text-slate-950">{title}</p>
              </BlockedUserInfoGroup>
              <BlockedUserInfoGroup title={COMMUNITY_URL}>
                <p className="truncate text-sm font-medium text-slate-950">
                  {networkUrl(network.name, `/community/${boardId}`)}
                </p>
              </BlockedUserInfoGroup>
              <BlockedUserInfoGroup title={COMMUNITY_OWNER}>
                <UsernameMenu
                  user={owner}
                  messageEnabled={false}
                  isOwnProfile={loggedInBoardUser.userId === owner.id}
                />
              </BlockedUserInfoGroup>
            </Stack>
            <BlockedMessage
              reason={loggedInBoardUser.user.blockFromBoard.reason}
            />
            <BlockedFromBoardExpandables
              reason={loggedInBoardUser.user.blockFromBoard.reason}
              networkName={network.title}
            />
            <p className="text-sm text-slate-700">
              For additional information on blocked status, please read{' '}
              <Link
                href={ARTICLE_LINK}
                target="_blank"
                rel="noreferrer"
                exact
                className="font-normal">
                this article in our Knowledge Base
              </Link>
              .
            </p>
            <p className="text-sm text-slate-700">
              For additional help please email us at{' '}
              <a href="mailto:support@yellowdig.com">support@yellowdig.com</a>
            </p>
          </Stack>
          <div className="flex justify-center">
            <Link
              className={cn(
                buttonVariants({ buttonType: 'primary', buttonSize: 'sm' }),
                'w-44 font-normal'
              )}
              href="/home">
              Go Home
            </Link>
          </div>
        </Stack>
      </BlockedUserCardWrapper>
    </Cover>
  )
}
